import { Nostalgist } from 'nostalgist';
import { locateGbaRam, type GbaRam } from './gba-ram';
import { deleteFile, loadFile, saveFile } from './storage';

export type GbaButton = 'A' | 'B' | 'L' | 'R' | 'START' | 'SELECT' | 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';

// Núcleo do mGBA para libretro (RetroArch compilado para WASM, sem threads), servido
// pelo próprio app em /core. Sem threads não precisamos de SharedArrayBuffer nem de
// isolamento cross-origin, que vários Telegram (Desktop, Web, Android) não liberam.
const CORE = 'mgba';
const SRAM_CHECK_MS = 10_000;
const STATE_SAVE_MS = 30_000;

// Teclado no desktop (mesmas teclas de antes): setas, X = A, Z = B, A/S = L/R,
// Enter = Start, Backspace = Select. Nada de atalhos do RetroArch (menu, F-keys…).
const RETROARCH_CONFIG = {
  input_player1_a: 'x',
  input_player1_b: 'z',
  input_player1_l: 'a',
  input_player1_r: 's',
  input_player1_start: 'enter',
  input_player1_select: 'backspace',
  input_menu_toggle: 'nul',
  input_toggle_fullscreen: 'nul',
  savestate_thumbnail_enable: false,
  video_font_enable: false,
  menu_enable_widgets: false,
  video_smooth: false,
} as const;

const BUTTON_NAME: Record<GbaButton, string> = {
  A: 'a',
  B: 'b',
  L: 'l',
  R: 'r',
  START: 'start',
  SELECT: 'select',
  UP: 'up',
  DOWN: 'down',
  LEFT: 'left',
  RIGHT: 'right',
};

const blobBytes = async (blob: Blob) => new Uint8Array(await blob.arrayBuffer());
// Os dados vêm do IndexedDB (ArrayBuffer comum); o tipo genérico só atrapalha o Blob.
const toBlob = (data: Uint8Array) => new Blob([data as Uint8Array<ArrayBuffer>]);

function sameBytes(a: Uint8Array | null, b: Uint8Array): boolean {
  if (!a || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * O RetroArch cria o próprio AudioContext e não o expõe. Capturamos na criação
 * para poder "destravar" o som com um toque (exigência do iOS).
 */
function captureAudioContexts(): { contexts: AudioContext[]; restore: () => void } {
  const contexts: AudioContext[] = [];
  const target = window as unknown as Record<string, unknown>;
  const originals = ['AudioContext', 'webkitAudioContext'].map((name) => [name, target[name]] as const);
  for (const [name, Original] of originals) {
    if (typeof Original !== 'function') continue;
    const Native = Original as typeof AudioContext;
    const Spy = function (this: unknown, options?: AudioContextOptions) {
      const context = new Native(options);
      contexts.push(context);
      return context;
    } as unknown as typeof AudioContext;
    Spy.prototype = Native.prototype;
    target[name] = Spy;
  }
  return {
    contexts,
    restore: () => originals.forEach(([name, Original]) => (target[name] = Original)),
  };
}

export class Emulator {
  private nostalgist: Nostalgist | null = null;
  private audio: AudioContext[] = [];
  private savedSram: Uint8Array | null = null;
  private timers: number[] = [];
  private muted = false;
  /** Progresso apagado: não salvar mais nada até recarregar a página. */
  private discarded = false;

  private constructor(private readonly canvas: HTMLCanvasElement) {}

  static async create(canvas: HTMLCanvasElement): Promise<Emulator> {
    return new Emulator(canvas);
  }

  get isRunning(): boolean {
    return this.nostalgist !== null;
  }

  readStoredRom(): Promise<Uint8Array | null> {
    return loadFile('rom');
  }

  /** Grava a ROM no aparelho. O "continuar de onde parou" é de outra ROM: descarta. */
  async storeRom(rom: Uint8Array): Promise<void> {
    await saveFile('rom', rom);
    await deleteFile('state');
  }

  async forgetRom(): Promise<void> {
    await deleteFile('rom');
    await deleteFile('state');
  }

  /** Já tem save do jogo ou estado guardado neste aparelho? */
  async hasProgress(): Promise<boolean> {
    const [sram, state] = await Promise.all([loadFile('sram'), loadFile('state')]);
    return sram !== null || state !== null;
  }

  /** Tem o estado do "continuar de onde parou"? (Some quando a ROM muda.) */
  async hasState(): Promise<boolean> {
    return (await loadFile('state')) !== null;
  }

  /** Começa um jogo a partir deste save (ex.: o "novo jogo" já com nome e gênero). */
  async installSave(sram: Uint8Array): Promise<void> {
    await saveFile('sram', sram);
    await deleteFile('state');
  }

  /** Apaga save e estado deste aparelho. Depois disso, recarregar a página. */
  async resetProgress(): Promise<void> {
    this.discarded = true;
    this.timers.forEach((timer) => window.clearInterval(timer));
    await deleteFile('sram');
    await deleteFile('state');
  }

  /** Liga o emulador com a ROM guardada, o save e o último estado (se houver). */
  async start(): Promise<void> {
    if (this.nostalgist) return;
    const [rom, sram, state] = await Promise.all([loadFile('rom'), loadFile('sram'), loadFile('state')]);
    if (!rom) throw new Error('Nenhuma ROM guardada neste aparelho.');
    this.savedSram = sram;

    const audio = captureAudioContexts();
    try {
      this.nostalgist = await Nostalgist.launch({
        element: this.canvas,
        core: CORE,
        rom: { fileName: 'firered.gba', fileContent: toBlob(rom) },
        sram: sram ? { fileName: 'firered.srm', fileContent: toBlob(sram) } : undefined,
        resolveCoreJs: () => 'core/mgba_libretro.js',
        resolveCoreWasm: () => 'core/mgba_libretro.wasm',
        retroarchConfig: { ...RETROARCH_CONFIG },
        // O núcleo ignora teclas digitadas em campos de texto (chat), então pode ouvir a página toda.
        respondToGlobalEvents: true,
      });
    } finally {
      audio.restore();
    }
    this.audio = audio.contexts;

    if (state) {
      try {
        await this.nostalgist.loadState(toBlob(state));
      } catch (error) {
        console.warn('Não deu para continuar de onde parou:', error);
      }
    }

    this.timers.push(
      window.setInterval(() => void this.persistSram(), SRAM_CHECK_MS),
      window.setInterval(() => void this.persistState(), STATE_SAVE_MS),
    );
  }

  /** Procura a RAM do GBA na memória do WASM. Só funciona com o jogo carregado. */
  locateRam(romHeader: Uint8Array): GbaRam | null {
    if (!this.nostalgist) return null;
    const module = this.nostalgist.getEmscriptenModule() as unknown as { HEAPU8: Uint8Array };
    return locateGbaRam(() => module.HEAPU8.buffer, romHeader);
  }

  press(button: GbaButton): void {
    this.nostalgist?.pressDown(BUTTON_NAME[button]);
  }

  release(button: GbaButton): void {
    this.nostalgist?.pressUp(BUTTON_NAME[button]);
  }

  pause(): void {
    this.nostalgist?.pause();
  }

  resume(): void {
    this.nostalgist?.resume();
  }

  /**
   * Alterna o avanço rápido. O RetroArch só tem "alternar" e às vezes perde um comando,
   * então quem chama confere a velocidade real depois (ver ensureSpeed no main.ts).
   */
  toggleFastForward(): void {
    this.nostalgist?.sendCommand('FAST_FORWARD');
  }

  setMuted(muted: boolean): void {
    if (!this.nostalgist || muted === this.muted) return;
    this.nostalgist.sendCommand('MUTE'); // alterna
    this.muted = muted;
  }

  /** O navegador só libera áudio depois de um toque/clique do usuário. */
  async resumeAudio(): Promise<void> {
    for (const context of this.audio) {
      if (context.state === 'running' || context.state === 'closed') continue;
      try {
        await context.resume();
      } catch {
        // iOS às vezes falha no estado "interrupted"; tenta de novo no próximo toque.
      }
    }
  }

  /** Salva tudo agora (ex.: quando a Mini App é minimizada). */
  async persistNow(): Promise<void> {
    await this.persistSram();
    await this.persistState();
  }

  /** O save do jogo (menu SAVE do FireRed) fica na SRAM: guarda quando muda. */
  private async persistSram(): Promise<void> {
    if (!this.nostalgist || this.discarded) return;
    try {
      const sram = await blobBytes(await this.nostalgist.saveSRAM());
      if (sram.length === 0 || sameBytes(this.savedSram, sram)) return;
      await saveFile('sram', sram);
      this.savedSram = sram;
    } catch (error) {
      console.warn('Não deu para guardar o save:', error);
    }
  }

  private async persistState(): Promise<void> {
    if (!this.nostalgist || this.discarded) return;
    try {
      const { state } = await this.nostalgist.saveState();
      await saveFile('state', await blobBytes(state));
    } catch (error) {
      console.warn('Não deu para guardar o estado:', error);
    }
  }
}
